import React, { useState } from 'react';

export interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  features: string[];
  popular?: boolean;
}

interface PlanSelectionProps {
  selectedPlan: string;
  onPlanSelect: (planId: string) => void;
  loading?: boolean;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'month',
    features: [
      '1,000 URLs per month',
      'Basic analytics',
      'Eddystone optimization',
      'API access'
    ]
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 299,
    period: 'month',
    features: [
      '50,000 URLs per month',
      'Advanced analytics',
      'Custom domains',
      'Bulk operations',
      'Priority support'
    ],
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 0, // Will display as "Custom"
    period: 'custom',
    features: [
      'Unlimited URLs',
      'White-label solution',
      'Dedicated support',
      'SLA guarantee',
      'Custom integrations'
    ]
  }
];

const PlanSelection: React.FC<PlanSelectionProps> = ({
  selectedPlan,
  onPlanSelect,
  loading = false
}) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`relative rounded-macos-lg border-2 p-6 cursor-pointer transition-all duration-300 backdrop-blur-sm ${
              selectedPlan === plan.id
                ? 'border-primary-500 bg-gradient-to-br from-primary-500/20 to-orange-500/20 shadow-macos-lg'
                : 'border-gray-600 bg-gray-700/50 hover:border-gray-500 hover:bg-gray-600/50'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''} ${
              plan.popular ? 'ring-2 ring-primary-500/30' : ''
            }`}
            onClick={() => !loading && onPlanSelect(plan.id)}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-primary-500 to-orange-500 text-white shadow-macos">
                  Most Popular
                </span>
              </div>
            )}
            
            <div className="text-center">
              <h4 className="text-lg font-semibold text-white">{plan.name}</h4>
              <div className="mt-3">
                <span className="text-3xl font-bold text-white">
                  {plan.period === 'custom' ? 'Custom' : `R${plan.price}`}
                </span>
                {plan.period !== 'custom' && (
                  <span className="text-sm text-gray-400">/{plan.period}</span>
                )}
              </div>
            </div>

            <ul className="mt-6 space-y-3">
              {plan.features.map((feature, index) => (
                <li key={index} className="flex items-start text-sm">
                  <svg className="flex-shrink-0 h-4 w-4 text-green-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-300">{feature}</span>
                </li>
              ))}
            </ul>

            {selectedPlan === plan.id && (
              <div className="absolute top-3 right-3">
                <div className="rounded-full bg-gradient-to-r from-primary-500 to-orange-500 p-1.5 shadow-macos">
                  <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlanSelection;